/* eslint-disable no-prototype-builtins */
import React, { useEffect, useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiPlusCircle, FiTrash2 } from "react-icons/fi";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../../components/Form";
import Button, { Color } from "../../../../../../../../../components/Form/Button";
import UITable, { Column } from "../../../../../../../../../components/Generic/UITable";
import { useAppState } from "../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { WarehouseSection } from "../../../../../../../../../models/warehouse.section";
import StorageBoxTypeService from "../../../../../../../../../services/storage.box.type.service";
import { CACHE_TIME, STALE_TIME } from "../../../../../../../../../util/constants";
import { RadioContainer, RadioButton, EmptyState } from "./styles";
import SelectMultiple from "Components/Form/SelectMultiple";

interface SectionTabProps {
  onViewMode?: boolean;
  sectionList?: WarehouseSection[];
  errors?: yup.ValidationError[];
  onUpdate: (sections: WarehouseSection[]) => void;
}

enum SectionAllowsOptionValues {
  LOOSE_ITEMS_VALUE = "LI",
  STORAGE_BOX_VALUE = "SB",
}
export function SectionTab({
  onViewMode = false,
  sectionList = [],
  errors = [],
  onUpdate,
}: SectionTabProps) {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [sections, setSections] = useState<WarehouseSection[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { data: storageBoxTypes, isFetching } = useQuery({
    queryKey: ["storage.box.type.list.in.warehouse"],
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    queryFn: async () => {
      const response = await StorageBoxTypeService.getAll({
        companyId: currentUser?.companyId,
        isActive: "1",
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data?.length) {
        return response.data;
      }

      return [];
    },
  });

  const disabledFields: boolean = isFetching || onViewMode;
  const errorsOnBodyTable = !!errors.filter((err) => err.path?.includes("sections["))?.length;

  const sectionAllowsOptions = [
    {
      name: translate("looseItems"),
      value: SectionAllowsOptionValues.LOOSE_ITEMS_VALUE,
    },
    {
      name: translate("storageBox"),
      value: SectionAllowsOptionValues.STORAGE_BOX_VALUE,
    },
  ];

  const columns: Column[] = [
    {
      title: translate("name"),
      columnName: "name",
      spacingColumns: "minmax(0, 4fr)",
      formatComponent: (value: string, rowIndex?: number) => (
        <Input
          disabled={onViewMode}
          name="sectionName"
          data-cy="sectionName"
          value={value}
          onChange={(event) => onChangeSection(event.target.value, "name", rowIndex)}
          error={fieldHasError(`sections[${rowIndex}].name`)}
          errorMessage={getErrorByField(`sections[${rowIndex}].name`)}
        />
      ),
    },
    {
      title: translate("contains"),
      spacingColumns: "minmax(0, 4fr)",
      columnName: "storageBoxTypes",
      formatComponent: (_, rowIndex?: number) => (
        <Select
          disabled={onViewMode}
          name="sectionContains"
          data-cy="sectionContains"
          value={onGetSectionContainsValue(rowIndex)}
          onChange={(event: SelectChangeEvent<any>) =>
            onChangeContainsValue(event.target.value, rowIndex)
          }
          error={fieldHasError(`sections[${rowIndex}].containsField`)}
          errorMessage={getErrorByField(`sections[${rowIndex}].containsField`)}
        >
          {sectionAllowsOptions?.map((option) => (
            <MenuItem key={option.name} value={option.value}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      title: translate("storageBoxType"),
      columnName: "storageBoxTypes",
      spacingColumns: "minmax(0, 4fr)",
      formatComponent: (_, rowIndex?: number) => {
        if (sections[Number(rowIndex)]?.allowsItemLoose) {
          return <EmptyState>---</EmptyState>;
        }

        return (
          <SelectMultiple
            loading={false}
            name="storageBoxTypeField"
            value={
              sections?.[Number(rowIndex)]?.storageBoxTypes
                ?.map((type) => type.id)
                ?.filter((id): id is number => typeof id === "number") || []
            }
            selecteds={
              sections?.[Number(rowIndex)]?.storageBoxTypes?.map((type) => ({
                key: Number(type.id),
                label: translate(type.name),
              })) || []
            }
            disabled={disabledFields || sections[Number(rowIndex)]?.allowsItemLoose}
            onChange={(e: SelectChangeEvent<any>) => {
              const rawValues = e.target.value;

              if (rawValues.includes(-2) || rawValues.includes(0) || rawValues.includes(-1)) return;
              const selectedIds = rawValues.map((val: string | number) => Number(val));
              const selectedOptions = (storageBoxTypes ?? []).filter((option) =>
                selectedIds.includes(option.id)
              );
              const clonedSections = JSON.parse(JSON.stringify(sections));

              clonedSections[Number(rowIndex)].storageBoxTypes = selectedOptions;
              setSections(clonedSections);
              onUpdate(clonedSections);
            }}
            error={fieldHasError(`sections[${rowIndex}].storageBoxTypes`)}
            errorMessage={getErrorByField(`sections[${rowIndex}].storageBoxTypes`)}
            items={
              storageBoxTypes?.map((option) => ({
                key: Number(option.id),
                label: translate(option.name),
              })) || []
            }
            onAddAll={() => {
              const allOptions =
                storageBoxTypes?.map((option) => ({
                  id: Number(option.id),
                  name: option.name,
                })) || [];
              const clonedSections = JSON.parse(JSON.stringify(sections));

              clonedSections[Number(rowIndex)].storageBoxTypes = allOptions;
              setSections(clonedSections);
              onUpdate(clonedSections);
            }}
            onRemoveAll={() => {
              const clonedSections = JSON.parse(JSON.stringify(sections));

              clonedSections[Number(rowIndex)].storageBoxTypes = [];
              setSections(clonedSections);
              onUpdate(clonedSections);
            }}
            renderInternalCount={false}
          />
        );
      },
    },
    {
      title: translate("default"),
      columnName: "isDefault",
      spacingColumns: "1fr",
      formatComponent: (value: string, rowIndex?: number) => (
        <RadioContainer>
          <RadioButton
            checked={Boolean(value)}
            disabled={onViewMode}
            data-cy="defaultSection"
            onChange={(event) => onChangeSection(event.target.checked, "isDefault", rowIndex)}
          />
        </RadioContainer>
      ),
    },
  ];

  const onChangeContainsValue = (value: string, rowIndex?: number) => {
    switch (value.toLowerCase()) {
      case SectionAllowsOptionValues.LOOSE_ITEMS_VALUE.toLowerCase():
        if (rowIndex !== undefined) {
          const clonedSections = _.cloneDeep(sections);

          if (clonedSections[rowIndex]) {
            clonedSections[rowIndex].allowsItemLoose = true;
            clonedSections[rowIndex].allowsStorageBox = false;
            clonedSections[rowIndex].storageBoxTypes = [];
          }
          setSections(clonedSections);
          onUpdate(clonedSections);
        }
        break;
      case SectionAllowsOptionValues.STORAGE_BOX_VALUE.toLowerCase():
        if (rowIndex !== undefined) {
          const clonedSections = _.cloneDeep(sections);

          if (clonedSections[rowIndex]) {
            clonedSections[rowIndex].allowsItemLoose = false;
            clonedSections[rowIndex].allowsStorageBox = true;
          }
          setSections(clonedSections);
          onUpdate(clonedSections);
        }
        break;
      default:
        break;
    }
  };

  const onGetSectionContainsValue = (rowIndex?: number) => {
    if (rowIndex !== undefined) {
      const section = sections?.[rowIndex];

      if (section) {
        const { allowsItemLoose, allowsStorageBox } = section;

        if (allowsItemLoose) return SectionAllowsOptionValues.LOOSE_ITEMS_VALUE;
        if (allowsStorageBox) return SectionAllowsOptionValues.STORAGE_BOX_VALUE;

        return "";
      }
    }

    return "";
  };

  const onChangeSection = (value: string | number | boolean, attribute: string, row?: number) => {
    const clonedSections = _.cloneDeep(sections);

    if (clonedSections?.length && row !== undefined) {
      const section = clonedSections[row] as any;

      if (section?.hasOwnProperty(attribute)) {
        switch (attribute) {
          case "isDefault":
            clonedSections.forEach((_, index) => {
              if (index !== row) {
                clonedSections[index].isDefault = false;
              } else {
                clonedSections[index].isDefault = Boolean(value);
              }
            });
            break;
          case "storageBoxTypes":
            if (section && value && !Number.isNaN(value)) {
              const storageBoxTypeId = Number(value);
              const storageBoxType = storageBoxTypes?.find((type) => type?.id === storageBoxTypeId);

              section.storageBoxTypes = storageBoxType ? [storageBoxType] : [];
            }
            break;
          default:
            section[attribute as keyof WarehouseSection] = value;
            break;
        }
      }

      setSections([...clonedSections]);
      onUpdate(clonedSections);
    }
  };

  const onAddNewSection = () => {
    const clonedSections = _.cloneDeep(sections);

    const newSection = {
      name: "",
      isDefault: !clonedSections.length,
      isActive: true,
      allowsItemLoose: false,
      allowsStorageBox: true,
      storageBoxTypes: undefined,
    } as WarehouseSection;

    clonedSections.push(newSection);

    setSections([...clonedSections]);
    onUpdate(clonedSections);
  };

  const onRemoveSection = (idx?: number) => {
    if (idx !== undefined) {
      const clonedSections = _.cloneDeep(sections);

      if (clonedSections[idx].isDefault) {
        const firstSectionThatIsNotDefaultIdx = clonedSections.findIndex(
          (section, index) => !section.isDefault && index !== idx
        );

        if (firstSectionThatIsNotDefaultIdx !== -1) {
          clonedSections[firstSectionThatIsNotDefaultIdx].isDefault = true;
        }
      }

      clonedSections.splice(idx, 1);

      setSections([...clonedSections]);
      onUpdate(clonedSections);
    }
  };

  const renderHeader = () => {
    if (!onViewMode) {
      return (
        <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
          <Button
            disabled={disabledFields}
            onClick={onAddNewSection}
            data-cy="buttonAddSection"
            customColor={Color.PRIMARY}
          >
            <FiPlusCircle size={18} className="mr-2" />
            {translate("createNewSection")}
          </Button>
        </Grid>
      );
    }

    return undefined;
  };

  const renderActionColumn = (idx?: number) => {
    return (
      <IconButton onClick={() => onRemoveSection(idx)}>
        <FiTrash2 size={16} color={colors.error400} />
      </IconButton>
    );
  };

  useEffect(() => {
    if (sectionList?.length) setSections(sectionList);
  }, [sectionList]);

  return (
    <UITable
      titleTable={translate("sections")}
      errors={getErrorByField("sections")}
      hasBodyErrors={errorsOnBodyTable}
      data={sections}
      checkField={false}
      isOnViewMode={onViewMode}
      columns={columns}
      headerActions={renderHeader()}
      actionColumn={renderActionColumn}
    />
  );
}
